import { useCollaboration } from '@/hooks/useCollaborations';
import { useEndpointTestRunGroup } from '@/hooks/useEndpointTestRunGroups';
import { humanScoreOf } from '@/utils/testRunResults';
import type { ComparisonCell, EndpointTestRunResult } from '@/types/endpointTests';

interface SideProps {
  label: string;
  testRunId: string;
  cell: ComparisonCell | null;
  /** Per-side extras (task 1180's scores table). */
  renderExtras?: (result: EndpointTestRunResult) => React.ReactNode;
}

/** One side of an expanded comparison row: its output and its human verdict. */
function Side({ label, testRunId, cell, renderExtras }: SideProps) {
  const { data: detail } = useEndpointTestRunGroup(cell ? testRunId : undefined);
  const result = detail?.results.find((candidate) => candidate.id === cell?.endpointTestRunId);
  const { data: collaboration, isLoading } = useCollaboration(result?.collaborationId ?? undefined);
  const human = result ? humanScoreOf(result) : undefined;

  return (
    <div className="min-w-0 space-y-2">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label} output</p>
      {!cell ? (
        <p className="text-xs text-gray-400">Not in this test.</p>
      ) : (
        <>
          <pre className="p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-48 whitespace-pre-wrap break-words">
            {isLoading || !detail ? 'Loading…' : collaboration?.output ? JSON.stringify(collaboration.output, null, 2) : '—'}
          </pre>
          <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="comparison-human-verdict">
            Human verdict:{' '}
            {human ? (
              <span className="font-medium">
                {human.outcome}
                {human.rationale ? ` — ${human.rationale}` : ''}
              </span>
            ) : (
              <span className="text-gray-400">none yet</span>
            )}
          </p>
          {result && renderExtras?.(result)}
        </>
      )}
    </div>
  );
}

interface ComparisonRowDetailProps {
  aId: string;
  bId: string;
  aLabel: string;
  bLabel: string;
  a: ComparisonCell | null;
  b: ComparisonCell | null;
  renderExtras?: (result: EndpointTestRunResult) => React.ReactNode;
}

/**
 * The scenario's input, once for both sides (task 1358): both results ran the
 * same scenario. These reads are the same queries the two `Side`s make, so
 * React Query serves them from one request each. The scenario as saved
 * (`endpointTest.input`) wins over the collaboration's input, as in the
 * result drawer.
 */
function useComparisonInput(aId: string, bId: string, a: ComparisonCell | null, b: ComparisonCell | null) {
  const { data: aDetail } = useEndpointTestRunGroup(a ? aId : undefined);
  const { data: bDetail } = useEndpointTestRunGroup(b ? bId : undefined);
  const aResult = aDetail?.results.find((candidate) => candidate.id === a?.endpointTestRunId);
  const bResult = bDetail?.results.find((candidate) => candidate.id === b?.endpointTestRunId);
  const scenarioInput = aResult?.endpointTest?.input ?? bResult?.endpointTest?.input ?? null;
  const { data: aCollaboration, isLoading: aLoading } = useCollaboration(
    scenarioInput == null ? (aResult?.collaborationId ?? undefined) : undefined,
  );
  const { data: bCollaboration, isLoading: bLoading } = useCollaboration(
    scenarioInput == null ? (bResult?.collaborationId ?? undefined) : undefined,
  );
  const input = scenarioInput ?? aCollaboration?.input ?? bCollaboration?.input ?? null;
  const loading = input == null && ((!!a && !aDetail) || (!!b && !bDetail) || aLoading || bLoading);
  return { input, loading };
}

/** Both outputs side by side, with both human verdicts (task 1178), under the input they were sent. */
export function ComparisonRowDetail({ aId, bId, aLabel, bLabel, a, b, renderExtras }: ComparisonRowDetailProps) {
  const { input, loading } = useComparisonInput(aId, bId, a, b);
  return (
    <div className="space-y-4 px-4 py-3 bg-gray-50/60 dark:bg-gray-900/40">
      <div className="min-w-0 space-y-2">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Input</p>
        <pre
          className="p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs font-mono text-gray-800 dark:text-gray-200 overflow-auto max-h-48 whitespace-pre-wrap break-words"
          data-testid="comparison-input"
        >
          {input != null ? JSON.stringify(input, null, 2) : loading ? 'Loading…' : '—'}
        </pre>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Side label={aLabel} testRunId={aId} cell={a} renderExtras={renderExtras} />
        <Side label={bLabel} testRunId={bId} cell={b} renderExtras={renderExtras} />
      </div>
    </div>
  );
}

export default ComparisonRowDetail;
